import { useState } from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import { IconButton, Paper, Stack, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from './languages';

type LanguageSelectorProps = {
	verticalOffset?: string;
};

const SelectorRoot = styled(Stack)<{ $verticalOffset: string }>(({ $verticalOffset }) => ({
	position: 'relative',
	marginTop: $verticalOffset,
}));

const SelectorIconButton = styled(IconButton)(({ theme }) => ({
	color: theme.palette.primary.main,
	padding: 0,
}));

const StyledContainer = styled(Paper)(({ theme }) => ({
	display: 'inline-flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: theme.spacing(0.75, 0.5),
	borderRadius: '16px',
	minWidth: '52px',
	gap: theme.spacing(0.5),
	position: 'absolute',
	top: 'calc(100% + 4px)',
	right: 0,
	zIndex: theme.zIndex.modal + 1,
}));

const LanguageButton = styled('button')<{ $active: boolean }>(({ theme, $active }) => ({
	border: 'none',
	background: 'transparent',
	cursor: 'pointer',
	padding: theme.spacing(0.5, 0.5),
	borderRadius: '8px',
	width: '100%',
	color: $active ? theme.palette.primary.main : theme.palette.text.primary,
	fontWeight: $active ? 700 : 500,
	fontSize: '1rem',
	lineHeight: 1.2,
	transition: 'background-color 0.2s ease, color 0.2s ease',
	'&:hover': {
		backgroundColor: theme.palette.action.hover,
	},
}));

const normalizeLanguage = (language: string) => language.toLowerCase().split('-')[0];

const LanguageSelector = ({ verticalOffset = '-10px' }: LanguageSelectorProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { i18n } = useTranslation();
	const currentLanguage = normalizeLanguage(i18n.language || 'pl');

	const handleChangeLanguage = (languageCode: string) => {
		if (languageCode !== currentLanguage) {
			i18n.changeLanguage(languageCode);
		}
		setIsOpen(false);
	};

	return (
		<SelectorRoot alignItems="center" spacing={0.5} $verticalOffset={verticalOffset}>
			<SelectorIconButton
				size="medium"
				onClick={() => setIsOpen((prevState) => !prevState)}
				aria-label="Toggle language selector"
				aria-expanded={isOpen}
			>
				<LanguageIcon fontSize="medium" />
			</SelectorIconButton>

			{isOpen && (
				<StyledContainer elevation={1}>
					{LANGUAGES.map((language) => (
						<LanguageButton
							key={language.code}
							type="button"
							$active={currentLanguage === language.code}
							onClick={() => handleChangeLanguage(language.code)}
						>
							<Typography component="span" variant="body2">
								{language.label}
							</Typography>
						</LanguageButton>
					))}
				</StyledContainer>
			)}
		</SelectorRoot>
	);
};

export default LanguageSelector;
